import m from '@bitstillery/mithril'
import {MithrilComponent} from '@bitstillery/mithril'
import {html_element} from '@bitstillery/common/lib/dom'

function ease_out_cubic(t: number): number {
    return 1 - (1 - t) ** 3
}

/** Caps duration so very large targets (e.g. stock lines) stay snappy. */
function default_duration_ms(target: number): number {
    return Math.min(2200, Math.round(800 + (target / 2000) * 800))
}

/**
 * Formatted numeric stat with optional scroll-triggered count-up.
 *
 * Internal helper of `Stat` — not a CMS catalog component and not exposed to view authors;
 * it renders just the number (and optional prefix/suffix) inside a `Stat`.
 *
 * When `animate` is true, state resets automatically when `target`, `start`, or `animate` change,
 * so callers do not need a Mithril `key` to replay after async data loads.
 */
export interface ScrollReplayCountAttrs {
    /** Inclusive end value shown after the animation (before `suffix`). */
    target: number
    /**
     * When true (default), count from `start` toward `target` the first time the element scrolls
     * into view. When false, show the final formatted value immediately (no intersection observer,
     * no ellipsis, no RAF).
     */
    animate?: boolean
    className?: string
    /** Animation length; default scales with `target` (capped ~2.2s). Ignored when `animate` is false. */
    duration_ms?: number
    /** Extra root margin passed to `IntersectionObserver`. */
    intersection_root_margin?: string
    intersection_threshold?: number | number[]
    /** Prepended while running and when done. */
    prefix?: string
    /** First value shown while counting (default 1). Use 0 with `target` 0 for a static zero. */
    start?: number
    /** Appended after the formatted value in the final state, e.g. `"+"` → `2,000+`. */
    suffix?: string | undefined
}

export class ScrollReplayCount extends MithrilComponent<ScrollReplayCountAttrs> {
    private a: ScrollReplayCountAttrs
    private nf = new Intl.NumberFormat(undefined)
    private phase: 'wait' | 'run' | 'done' = 'wait'
    private display_num = 1
    private io: IntersectionObserver | null = null
    private raf_id = 0
    private t0 = 0
    /** DOM root for the observer; set in `oncreate`. */
    private dom_el: HTMLElement | null = null
    /** After `target`/`start`/`animate` change, re-arm the observer on the next `onupdate`. */
    private pending_arm_io = false
    /** Fingerprint of attrs that define animation baseline; avoids remount via `key`. */
    private last_baseline_key = ''

    constructor(vnode: m.Vnode<ScrollReplayCountAttrs>) {
        super()
        this.a = vnode.attrs
    }

    private cfg() {
        const a = this.a
        const start = a.start ?? 1
        const target = a.target
        return {
            animate: a.animate !== false,
            start,
            target,
            duration_ms: a.duration_ms ?? default_duration_ms(target),
            prefix: a.prefix ?? '',
            suffix: a.suffix ?? '',
            root_margin: a.intersection_root_margin ?? '0px 0px -8% 0px',
            threshold: a.intersection_threshold ?? 0.15,
        }
    }

    private baseline_key(a: ScrollReplayCountAttrs): string {
        return `${a.target}\0${a.start ?? 1}\0${a.animate !== false}`
    }

    private teardown(): void {
        this.io?.disconnect()
        this.io = null
        if (this.raf_id) {
            cancelAnimationFrame(this.raf_id)
            this.raf_id = 0
        }
    }

    private apply_baseline_state(): void {
        this.teardown()
        const c = this.cfg()
        if (!c.animate) {
            this.phase = 'done'
            this.display_num = c.target === 0 ? 0 : c.target
            return
        }
        if (typeof window === 'undefined') {
            this.phase = 'done'
            this.display_num = c.target === 0 ? 0 : c.target
            return
        }
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.phase = 'done'
            this.display_num = c.target
            return
        }
        if (c.target === 0) {
            this.phase = 'done'
            this.display_num = 0
            return
        }
        if (c.target < c.start) {
            this.phase = 'done'
            this.display_num = c.target
            return
        }
        this.phase = 'wait'
        this.display_num = c.start
        this.t0 = 0
        this.pending_arm_io = this.dom_el != null
    }

    override oninit() {
        this.apply_baseline_state()
        this.last_baseline_key = this.baseline_key(this.a)
    }

    override onbeforeupdate(vnode: m.Vnode<ScrollReplayCountAttrs>) {
        const next = vnode.attrs
        const next_key = this.baseline_key(next)
        if (next_key !== this.last_baseline_key) {
            this.last_baseline_key = next_key
            this.a = next
            this.apply_baseline_state()
        } else {
            this.a = next
        }
    }

    override onremove() {
        this.teardown()
    }

    private format_num(n: number): string {
        return this.nf.format(n)
    }

    private arm_intersection_observer(): void {
        this.io?.disconnect()
        this.io = null
        const c = this.cfg()
        if (!c.animate || this.phase !== 'wait' || !this.dom_el) {
            return
        }
        if (typeof IntersectionObserver === 'undefined') {
            this.phase = 'done'
            this.display_num = c.target
            m.redraw()
            return
        }
        this.io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (e.isIntersecting && this.phase === 'wait') {
                        this.start_animation()
                    }
                }
            },
            {root: null, rootMargin: c.root_margin, threshold: c.threshold},
        )
        this.io.observe(this.dom_el)
    }

    private start_animation = (): void => {
        if (this.phase !== 'wait') {
            return
        }
        const {start, target, duration_ms} = this.cfg()

        if (target === 0) {
            this.phase = 'done'
            this.display_num = 0
            m.redraw()
            return
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.phase = 'done'
            this.display_num = target
            m.redraw()
            return
        }
        if (target < start) {
            this.phase = 'done'
            this.display_num = target
            m.redraw()
            return
        }
        this.phase = 'run'
        this.display_num = start
        this.t0 = 0
        m.redraw()

        const span = target - start
        const tick = (now: number) => {
            if (this.t0 === 0) {
                this.t0 = now
            }
            const elapsed = now - this.t0
            const t = Math.min(1, elapsed / duration_ms)
            const eased = ease_out_cubic(t)
            const next_display = Math.max(start, Math.min(target, Math.floor(start + span * eased)))

            const done = t >= 1
            if (done) {
                this.phase = 'done'
                this.display_num = target
                this.raf_id = 0
                m.redraw()
                return
            }

            if (next_display !== this.display_num) {
                this.display_num = next_display
                m.redraw()
            }

            this.raf_id = requestAnimationFrame(tick)
        }
        this.raf_id = requestAnimationFrame(tick)
    }

    view() {
        const c = this.cfg()
        const {prefix, suffix, target} = c

        let display_text: string
        if (!c.animate || this.phase === 'done') {
            display_text = `${prefix}${this.format_num(target)}${suffix}`
        } else if (this.phase === 'wait') {
            display_text = '\u2026'
        } else {
            display_text = `${prefix}${this.format_num(this.display_num)}${suffix}`
        }

        const class_name = this.a.className ?? 'cms-stat-value scroll-replay-count'

        return (
            <span
                className={class_name}
                oncreate={(v: m.VnodeDOM) => {
                    this.dom_el = html_element(v.dom)
                    this.arm_intersection_observer()
                }}
                onupdate={() => {
                    if (this.pending_arm_io) {
                        this.pending_arm_io = false
                        this.arm_intersection_observer()
                    }
                }}
            >
                {display_text}
            </span>
        )
    }
}
